import { useEffect, useRef, useState } from "react";

// Robust, cross-device count-up.
// Uses a native IntersectionObserver (works reliably on mobile, no hover/click
// dependency), runs exactly once, and always settles on the final value.
export const CountUp = ({ end, prefix = "", suffix = "", duration = 2200, className = "" }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const startTs = performance.now();
      const tick = (now) => {
        const p = Math.min((now - startTs) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setVal(Math.round(eased * end));
        if (p < 1) requestAnimationFrame(tick);
        else setVal(end); // lock final value
      };
      requestAnimationFrame(tick);
    };

    // Run immediately if the element is already within the viewport on mount.
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh * 0.92 && rect.bottom > 0) {
      run();
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      // Fallback for very old browsers: show final value.
      setVal(end);
      startedRef.current = true;
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            run();
            io.disconnect();
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{val.toLocaleString("en-IN")}{suffix}
    </span>
  );
};

export default CountUp;
